import React, { useState } from 'react';
import { css } from '../styled-system/css/index';
import { useSearchParams } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import useCatalogProducts from '../hooks/useCatalogProducts';

const pageStyle = css({
  maxW: '800px',
  mt: '40px',
  mb: '40px',
  ml: 'auto',
  mr: 'auto',
  p: '0 20px',
});

const headerStyle = css({
  color: '#111827',
  mb: '16px',
});

const formStyle = css({
  display: 'grid',
  gap: '12px',
  bg: '#fff',
  p: '20px',
  border: '1px solid #e5e7eb',
  borderRadius: '10px',
});

const rowStyle = css({
  display: 'grid',
  gap: '12px',
  gridTemplateColumns: '1fr 1fr',
  selectors: { '@media (max-width: 700px)': { gridTemplateColumns: '1fr' } },
});

const inputStyle = css({
  p: '12px',
  borderRadius: '8px',
  border: '1px solid #e5e7eb',
  bg: '#fff',
  color: '#111827',
});

const textareaStyle = css({
  p: '12px',
  borderRadius: '8px',
  border: '1px solid #e5e7eb',
  bg: '#fff',
  color: '#111827',
  minH: '140px',
});

const btnStyle = css({
  p: '12px 18px',
  borderRadius: '6px',
  border: 'none',
  bg: '#197bbd',
  color: '#ffffff',
  fontWeight: 700,
  cursor: 'pointer',
  w: 'fit-content',
});

const formatCents = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;

const Contact = () => {
  const [searchParams] = useSearchParams();
  const { items } = useCart();
  const products = useCatalogProducts();
  const topic = searchParams.get('topic');
  const isBulkOrder = topic === 'bulk-order';
  const isEmailOrder = topic === 'email-order';
  const product = products.find((item) => item.id === searchParams.get('product'));
  const requestedAmount = Math.min(9999, Math.max(1, Number.parseInt(searchParams.get('amount'), 10) || 1));
  const requestedItems = product
    ? [{
      name: product.description || product.name,
      quantity: requestedAmount,
      unitAmount: typeof product.price === 'number' ? Math.round(product.price * 100) : null,
    }]
    : items;
  const itemLines = requestedItems.length
    ? requestedItems.map((item) => {
      const lineTotal = Number.isInteger(item.unitAmount) ? ` - ${formatCents(item.unitAmount * item.quantity)}` : '';
      return `- ${item.name} x ${item.quantity}${lineTotal}`;
    }).join('\n')
    : '- Product(s) and requested amount: ';
  const requestSubtotal = requestedItems.reduce(
    (sum, item) => sum + (Number.isInteger(item.unitAmount) ? item.unitAmount * item.quantity : 0),
    0,
  );
  const subtotalLine = requestSubtotal > 0 ? `\n\nCart subtotal: ${formatCents(requestSubtotal)}` : '';
  const requestMessage = `${isBulkOrder ? 'Please provide a bulk order quote for' : 'I am interested in ordering'}:\n\n${itemLines}${subtotalLine}\n\nPlease send me a quote for this order.\n\nShipping address:\nName:\nCompany:\nStreet address:\nCity, State ZIP:\nPhone:`;
  const [form, setForm] = useState(() => ({
    name: '',
    email: '',
    subject: isBulkOrder
      ? `Bulk order quote${product ? `: ${product.name}` : ' request'}`
      : isEmailOrder
        ? `Order quote request${product ? `: ${product.name}` : ''}`
        : '',
    message: isBulkOrder || isEmailOrder ? requestMessage : '',
  }));
  const [status, setStatus] = useState({ type: '', text: '' });
  const [loading, setLoading] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus({ type: '', text: '' });
    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || 'Failed to send');
      setStatus({
        type: 'ok',
        text: isBulkOrder
          ? 'Your quote request was sent.'
          : isEmailOrder
            ? 'Your order request was sent. We will contact you with a quote.'
            : 'Thanks! We will get back to you soon.',
      });
      setForm({ name: '', email: '', subject: '', message: '' });
    } catch (e) {
      setStatus({ type: 'err', text: e.message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={pageStyle}>
      <h1 className={headerStyle}>{isBulkOrder ? 'Request a Bulk Order Quote' : isEmailOrder ? 'Request an Order Quote' : 'Contact Us'}</h1>
      {status.text ? (
        <div style={{ color: status.type === 'ok' ? '#0f766e' : '#b91c1c', marginBottom: 10 }}>{status.text}</div>
      ) : null}
      <form className={formStyle} onSubmit={onSubmit}>
        <div className={rowStyle}>
          <input className={inputStyle} name="name" placeholder="Name" value={form.name} onChange={onChange} required />
          <input className={inputStyle} name="email" type="email" placeholder="Email" value={form.email} onChange={onChange} required />
        </div>
        <input className={inputStyle} name="subject" placeholder="Subject" value={form.subject} onChange={onChange} />
        <textarea className={textareaStyle} name="message" placeholder="Message" value={form.message} onChange={onChange} required />
        <button className={btnStyle} type="submit" disabled={loading}>{loading ? 'Sending...' : isBulkOrder || isEmailOrder ? 'Send Quote Request' : 'Send Message'}</button>
      </form>
    </div>
  );
};

export default Contact;
